/**
 * Image Props Transformer
 * 
 * Transforms nested image objects (from NESTED fields with src/alt) into separate URL and Alt props
 * for devlink components that expect separate props (e.g., tab1ImgUrl + tab1ImgAlt).
 * Also applies Cloudinary URL transformation to convert relative paths to full Cloudinary URLs.
 * 
 * Structure Detection:
 *   - Props containing nested objects with { src, alt } or { src } fields
 * 
 * Transformation Examples:
 *   Input:  { tab1ImageUrl: { src: "https://...", alt: "description" } }
 *   Output: { tab1ImageUrl: "https://...", tab1ImageAlt: "description" }
 * 
 *   Input:  { customerLogo: { src: "https://...", alt: "Company logo" } }
 *   Output: { customerLogo: "https://...", customerLogoAlt: "Company logo" }
 * 
 *   Input:  { tab1HeadshotClientImage: { src: "https://...", alt: "Headshot" } }
 *   Output: { tab1HeadshotClientImage: "https://...", tab1HeadshotClientImageAlt: "Headshot" }
 * 
 *   Input:  { logoLink: { src: "https://...", alt: "Logo" } }
 *   Output: { logoLink: "https://...", logoAlt: "Logo" }
 */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
const { getCloudinaryUrlSrc } = require('/services/community/cloudinary-service');
/**
 * Checks if a value is a nested image object (has src field)
 * @param {*} value - Value to check
 * @returns {boolean} True if value is an image object
 */ function isImageObject(value) {
    if (!value || typeof value !== 'object') {
        return false;
    }
    return 'src' in value && typeof value.src === 'string';
}
/**
 * Determines the Alt prop name based on the image prop name
 * @param {string} imagePropName - The image prop name
 * @returns {string} The corresponding Alt prop name
 */ function getAltPropName(imagePropName) {
    // Handle props ending with URL/Url (case variations)
    if (/Url$/i.test(imagePropName)) {
        return imagePropName.replace(/Url$/i, 'Alt');
    }
    // Handle props ending with Link (case variations)
    if (/Link$/i.test(imagePropName)) {
        return imagePropName.replace(/Link$/i, 'Alt');
    }
    // For all other props, append Alt
    return `${imagePropName}Alt`;
}
/**
 * Converts an image src to a full Cloudinary URL
 * @param {string} src - The image source (can be relative path or full URL)
 * @returns {string} Full Cloudinary URL
 */ function transformImageSrc(src) {
    if (!src || typeof src !== 'string') {
        return src;
    }
    // Skip transformation if already a full URL
    if (src.startsWith('http://') || src.startsWith('https://')) {
        return src;
    }
    // Transform relative paths to full Cloudinary URLs
    return getCloudinaryUrlSrc(src);
}
/**
 * Transforms nested image props to separate URL and Alt props
 * Also applies Cloudinary URL transformation to image sources
 * @param {Object} props - Component props object
 * @returns {Object} Transformed props with separate URL and Alt values
 */ function transformImageProps(props) {
    if (!props || typeof props !== 'object') {
        return props;
    }
    const transformed = _object_spread({}, props);
    Object.keys(props).forEach((propName)=>{
        const propValue = props[propName];
        // Check if prop has nested image structure with src field
        if (isImageObject(propValue)) {
            const altPropName = getAltPropName(propName);
            // Transform: extract src, apply Cloudinary transformation, and extract alt
            const transformedSrc = transformImageSrc(propValue.src);
            transformed[propName] = transformedSrc;
            transformed[altPropName] = propValue.alt || '';
        }
    });
    return transformed;
}
module.exports = {
    transformImageProps,
    isImageObject,
    getAltPropName
};
